import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Button, Card, Typography } from "@mui/material";
import { useMemo } from "react";
import { AppColors } from "../../../../../util/AppColors";
import { mealTypes } from "../../../../../util/dropList";
import { canWriteModule } from "../../../../../util/roleModuleAccess";

const PrescriptionCard = ({ item, onEdit, onDelete }) => {
  const canManageDashboard = canWriteModule("dashboard");
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { clinicStocks, clinicId } = useMemo(() => {
    if (!mandateDataRaw) return { clinicStocks: [], clinicId: null };
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinicStocks: Array.isArray(reqData?.stocks) ? reqData.stocks : [],
        clinicId: reqData?.clinic?.id ?? null,
      };
    } catch {
      return { clinicStocks: [], clinicId: null };
    }
  }, [mandateDataRaw]);

  const reqStock = useMemo(() => {
    if (!item?.stockId) return null;
    return clinicStocks?.find((stc) => stc?.stockId === item?.stockId) ?? null;
  }, [item?.stockId, clinicStocks]);

  const selectedBatch = useMemo(() => {
    if (!reqStock) return null;
    const batches =
      reqStock?.stockBatch?.filter(
        (btch) => btch?.createdBy === clinicId && btch?.approved,
      ) ?? [];
    if (item?.batchId) {
      return (
        batches.find((bat) => String(bat?.batchId) === String(item?.batchId)) ??
        batches?.[0] ??
        null
      );
    }
    return batches?.[0] ?? null;
  }, [reqStock, item?.batchId, clinicId]);

  const isControlledSubstance = useMemo(() => {
    if (!reqStock) return false;
    const isScheduleH1 =
      (reqStock?.drugSchedule ?? "")?.toLowerCase() === "schedule h1";
    const isNdps =
      reqStock?.ndps === true ||
      reqStock?.ndps === "Y" ||
      reqStock?.ndps === "y";
    return isScheduleH1 || isNdps;
  }, [reqStock]);

  const frequencyLabel = Array.isArray(item?.frequency)
    ? item.frequency
        .map((freq) => freq?.name ?? freq?.value ?? freq)
        .filter(Boolean)
        .join(", ")
    : item?.frequency;

  const routeLabel =
    typeof item?.route === "object"
      ? (item?.route?.name ?? item?.route?.value)
      : item?.route;

  const mealLabel =
    mealTypes?.find((ml) => ml?.value === item?.meal)?.name || item?.meal;

  const uomName =
    selectedBatch?.salesUom ||
    selectedBatch?.packageUom ||
    selectedBatch?.qtyMapName ||
    selectedBatch?.uomMapName ||
    item?.uomName ||
    item?.packageUom ||
    "";

  return (
    <Card
      sx={{
        borderRadius: "8px",
        boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.06)",
        border: "1px solid #e0e0e0",
        p: 2,
        mb: 1.5,
        backgroundColor: "#ffffff",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
      >
        <Typography
          sx={{
            fontSize: "15px",
            fontWeight: 700,
            color: "#212121",
          }}
        >
          {item?.plName}
        </Typography>

        <Box display="flex" alignItems="center" gap={1.5}>
          {/* {item?.qty ? (
            <Typography
              sx={{
                fontSize: "13px",
                color: "#616161",
                fontWeight: 500,
              }}
            >
              Qty: {item.qty}
            </Typography>
          ) : null} */}

          {isControlledSubstance ? (
            <Box
              sx={{
                backgroundColor: "#ffebee",
                color: "#d32f2f",
                fontSize: "12px",
                fontWeight: 600,
                px: 1.2,
                py: 0.3,
                borderRadius: "4px",
                border: "1px solid #ffcdd2",
              }}
            >
              Controlled substance
            </Box>
          ) : null}

          {canManageDashboard ? (
            <>
              <Button
                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                onClick={() => onEdit(item)}
                sx={{
                  color: AppColors.appPrimary || "#0054A6",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  minWidth: "auto",
                  p: "4px 8px",
                  whiteSpace: "nowrap",
                }}
              >
                Edit
              </Button>

              <Button
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 18 }} />}
                onClick={() => onDelete(item?.detailId)}
                sx={{
                  color: "#d32f2f",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  minWidth: "auto",
                  p: "4px 8px",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    backgroundColor: "rgba(211, 47, 47, 0.04)",
                  },
                }}
              >
                Delete
              </Button>
            </>
          ) : null}
        </Box>
      </Box>

      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
        {item?.dosage ? (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#616161",
              fontSize: "11px",
              fontWeight: 500,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            Dose: {item.dosage}
            {selectedBatch?.packageUom ? ` ${selectedBatch.packageUom}` : ""}
          </Box>
        ) : null}

        {routeLabel ? (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#616161",
              fontSize: "11px",
              fontWeight: 500,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            Route: {routeLabel}
          </Box>
        ) : null}

        {frequencyLabel ? (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#616161",
              fontSize: "11px",
              fontWeight: 500,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            Freq: {frequencyLabel}
          </Box>
        ) : null}

        {item?.duration ? (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#616161",
              fontSize: "11px",
              fontWeight: 500,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            Duration: {item.duration} days
          </Box>
        ) : null}

        {mealLabel ? (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#616161",
              fontSize: "11px",
              fontWeight: 500,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            Meal: {mealLabel}
          </Box>
        ) : null}

        {item?.remark ? (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#616161",
              fontSize: "11px",
              fontWeight: 500,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            Remark: {item.remark}
          </Box>
        ) : null}
      </Box>
    </Card>
  );
};

export default PrescriptionCard;
